"use client";

import {
	BUYER_DASHBOARD_QUERY,
	BUYER_DASHBOARD_SUGGESTION_LIST_QUERY,
	BUYER_OFFERS_QUERY,
} from "@/gql/buyer-dashboard";
import { SELLER_UNITS_AND_PROJECTS_QUERY } from "@/gql/sup";
import { GET_UNREAD_COUNTS_FOR_BUYER_GROUP } from "@/lib/graphql/dialog-queries";
import {
	CategorizedProperties,
	DashboardCounts,
	DashboardTab,
	PropertyDetails,
	SuggestedProperty,
} from "@/types/buyer-dashboard";
import { BuyerProject } from "@/types/buyerProject";
import { BuyerUnit } from "@/types/buyerUnit";
import {
	categorizeItem,
	createEmptyCategories,
	createLookupMaps,
	enhanceItem,
	extractActiveIds,
} from "@/utils/buyer-dashboard";
import { logger } from "@/utils/logger";
import { useQuery } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "./use-auth";
import { useRelationship } from "./use-relationship";
import { useShortlistedProperties } from "./use-shortlisted-properties";

interface UseBuyerDashboardOptions {
	activeTab?: DashboardTab;
}

export function useBuyerDashboard({ activeTab = "suggested" }: UseBuyerDashboardOptions = {}) {
	const { fetchFreshUserData } = useAuth();
	const { user, buyerGroupId, isSeller, isAuthenticated } = useRelationship();
	const [isRefreshingUserData, setIsRefreshingUserData] = useState(false);

	const shouldSkip = !isAuthenticated || !buyerGroupId || isSeller;

	// === QUERIES ===

	const {
		data: buyerData,
		loading: buyerLoading,
		error: buyerError,
		refetch,
	} = useQuery(BUYER_DASHBOARD_QUERY, {
		variables: { params: { groupOne: { id: buyerGroupId } } },
		skip: shouldSkip,
		fetchPolicy: "cache-and-network",
		errorPolicy: "all",
	});

	const {
		data: suggestedData,
		loading: suggestedLoading,
		error: suggestedError,
	} = useQuery(BUYER_DASHBOARD_SUGGESTION_LIST_QUERY, {
		variables: {
			params: {
				getSuggestedProperties: { buyerId: buyerGroupId, limit: 100, skip: 0, status: "pending" },
			},
		},
		skip: shouldSkip,
		fetchPolicy: "cache-and-network",
		errorPolicy: "all",
	});

	const { data: offersData } = useQuery(BUYER_OFFERS_QUERY, {
		variables: { params: { getBuyerOffers: { buyerGroupId } } },
		skip: shouldSkip,
		fetchPolicy: "cache-and-network",
		errorPolicy: "all",
	});

	const { data: unreadCountsData } = useQuery(GET_UNREAD_COUNTS_FOR_BUYER_GROUP, {
		variables: { buyerGroupId },
		skip: shouldSkip,
		fetchPolicy: "cache-and-network",
		errorPolicy: "all",
	});

	// Fetch shortlisted projects (updates store)
	useShortlistedProperties(buyerGroupId);

	// === HANDLE GROUP NOT FOUND ERROR ===

	useEffect(() => {
		const isGroupNotFoundError = buyerError?.graphQLErrors?.some(err => err.message?.includes("Group not found"));

		if (isGroupNotFoundError && !isRefreshingUserData) {
			setIsRefreshingUserData(true);
			logger.warn("Group not found error detected, refreshing user data...", { buyerGroupId });
			fetchFreshUserData().finally(() => setIsRefreshingUserData(false));
		}
	}, [buyerError, buyerGroupId, fetchFreshUserData, isRefreshingUserData]);

	// === CATEGORIZE BASE DATA ===

	const { categorizedBaseData, activeUnitIds, activeProjectIds } = useMemo(() => {
		const groupOne = buyerData?.user?.groupOne;
		if (!groupOne) {
			return {
				categorizedBaseData: createEmptyCategories(),
				activeUnitIds: [] as string[],
				activeProjectIds: [] as string[],
			};
		}

		const buyerUnits: BuyerUnit[] = groupOne.buyerUnits || [];
		const buyerProjects: BuyerProject[] = groupOne.buyerProjects || [];
		const suggestedProperties = suggestedData?.buyer?.getSuggestedProperties || [];

		// Categorize all items
		const categories = createEmptyCategories();
		buyerUnits.forEach((item: BuyerUnit) => categorizeItem(item, categories));
		buyerProjects.forEach((item: BuyerProject) => categorizeItem(item, categories));
		suggestedProperties.forEach((p: SuggestedProperty) => categories.suggested.push(p));

		// Extract IDs for active tab
		const { unitIds, projectIds } = extractActiveIds(categories, activeTab);

		return {
			categorizedBaseData: categories,
			activeUnitIds: unitIds,
			activeProjectIds: projectIds,
		};
	}, [buyerData?.user?.groupOne, suggestedData?.buyer?.getSuggestedProperties, activeTab]);

	// === FETCH PROPERTY DETAILS FOR ACTIVE TAB ===

	const supQueryVariables = {
		params: {
			details: {
				ids: activeUnitIds,
				projectIds: activeProjectIds,
				type: "sp",
			},
		},
	};

	const {
		data: propertiesData,
		loading: propertiesLoading,
		error: propertiesError,
	} = useQuery(SELLER_UNITS_AND_PROJECTS_QUERY, {
		variables: supQueryVariables,
		skip: activeUnitIds.length === 0 && activeProjectIds.length === 0,
		fetchPolicy: "cache-and-network",
		errorPolicy: "all",
	});

	if (propertiesError) {
		logger.graphqlError(propertiesError, "SUP_QUERY", supQueryVariables);
	}

	// === ENHANCE DATA WITH PROPERTY DETAILS ===

	const categorizedData = useMemo((): CategorizedProperties => {
		const propertyDetails: PropertyDetails[] = propertiesData?.sellerUnitsAndProjects?.details || [];
		const buyerOffers = offersData?.buyer?.getBuyerOffers || [];
		const unreadCounts = unreadCountsData?.dialog?.unreadCountsForBuyerGroup || [];

		const maps = createLookupMaps(propertyDetails, activeProjectIds, buyerOffers, unreadCounts);

		return {
			shortlisted: categorizedBaseData.shortlisted.map(item => enhanceItem(item, maps)),
			interested: categorizedBaseData.interested.map(item => enhanceItem(item, maps)),
			visited: categorizedBaseData.visited.map(item => enhanceItem(item, maps)),
			rejected: categorizedBaseData.rejected.map(item => enhanceItem(item, maps)),
			suggested: categorizedBaseData.suggested.map(item => enhanceItem(item, maps)),
		};
	}, [categorizedBaseData, propertiesData, offersData, activeProjectIds, unreadCountsData]);

	// === COUNTS ===

	const counts = useMemo(
		(): DashboardCounts => ({
			total: Object.values(categorizedBaseData).flat().length,
			shortlisted: categorizedBaseData.shortlisted.length,
			interested: categorizedBaseData.interested.length,
			visited: categorizedBaseData.visited.length,
			rejected: categorizedBaseData.rejected.length,
			suggested: categorizedBaseData.suggested.length,
		}),
		[categorizedBaseData],
	);

	// === DERIVED VALUES ===

	const decisionMaker = useMemo(
		() =>
			buyerData?.user?.groupOne?.members?.find(
				(member: { designation: string }) => member.designation === "Decision Maker",
			),
		[buyerData?.user?.groupOne?.members],
	);

	const isAgreementDone = useMemo(
		() => buyerData?.user?.groupOne?.processDetails?.[1]?.status === "completed",
		[buyerData?.user?.groupOne?.processDetails],
	);

	// === RETURN ===

	return {
		data: categorizedData,
		isLoading: buyerLoading || suggestedLoading,
		isEnhancedDataLoading: propertiesLoading,
		error: buyerError || suggestedError,
		isAuthenticated,
		buyerGroupId,
		hasUserData: !!user,
		// Raw data for debugging
		rawBuyerData: buyerData,
		rawPropertiesData: propertiesData,
		rawBuyerUnits: buyerData?.user?.groupOne?.buyerUnits || [],
		rawBuyerProjects: buyerData?.user?.groupOne?.buyerProjects || [],
		statuses: { isAgreementDone },
		groups: { decisionMaker },
		counts,
		refetch,
	};
}
